// CHART-HOWIWORK (Andrei, 2026-10-27): "How I work" on a DE's Overview — its process in plain
// words (steps, when it asks a person, which agents it uses). Anyone who sees the DE reads it; an
// owner or admin, or the DE's supervisor, edits it in a modal (the page stays). The DE receives it
// at every run start, on top of its container's own files. Every save is a version: History opens
// them in a modal (who and when). Nothing written yet says so; no sample text.

import { ApiError } from "@companyos/sdk";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  Textarea,
  toast,
  workerName,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { HistoryIcon, PencilIcon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { DeProfile } from "../../api/types";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { keys } from "../queries";
import { LoadError } from "../screen-parts";
import { Empty, Expandable, Part } from "./de-about";
import { problemText } from "./de-status";

const MAX = 20000;

type HowIWorkView = NonNullable<DeProfile["how_i_work"]>;
type Author = NonNullable<HowIWorkView["updated_by"]>;

const byLine = (a: Author | null) =>
  a?.name ?? (a?.kind === "system" ? "CompanyOS" : a?.kind === "agency_user" ? "Agency" : null);

export function HowIWork({
  api,
  company,
  de,
}: {
  api: Api;
  company: CompanyAccess;
  de: DeProfile;
}) {
  const [editing, setEditing] = useState(false);
  const [history, setHistory] = useState(false);
  const view: HowIWorkView = de.how_i_work ?? {
    text: null,
    version: 0,
    updated_at: null,
    updated_by: null,
    can_edit: false,
  };
  const name = workerName(de.display_name);
  const edit = view.can_edit ? (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 text-xs font-normal tracking-normal normal-case"
      onClick={() => setEditing(true)}
      data-slot="how-i-work-edit"
    >
      <PencilIcon aria-hidden="true" />
      Edit
    </Button>
  ) : null;
  const historyButton =
    view.version > 0 ? (
      <Button
        variant="ghost"
        size="sm"
        className="h-7 text-xs font-normal tracking-normal normal-case"
        onClick={() => setHistory(true)}
        data-slot="how-i-work-history"
      >
        <HistoryIcon aria-hidden="true" />
        History
      </Button>
    ) : null;
  const who = byLine(view.updated_by);
  return (
    <>
      {editing ? (
        <HowIWorkDialog
          api={api}
          company={company}
          de={de}
          view={view}
          onClose={() => setEditing(false)}
        />
      ) : null}
      {history ? (
        <HowIWorkHistory
          api={api}
          company={company}
          deId={de.membership_id}
          name={name}
          onClose={() => setHistory(false)}
        />
      ) : null}
      <Part
        title="How I work"
        slot="de-how-i-work"
        info="Its process: steps, when it asks a person, which agents it uses. Sent to it at the start of every run, on top of its own instructions. Edited by an admin or its supervisor."
        actions={
          edit || historyButton ? (
            <>
              {historyButton}
              {edit}
            </>
          ) : undefined
        }
      >
        {view.text ? (
          <div className="flex min-w-0 flex-col gap-1">
            <Expandable text={view.text} slot="how-i-work-text" />
            {view.updated_at ? (
              <p className="text-xs text-muted-foreground" data-slot="how-i-work-meta">
                Version {view.version} · {formatDateTime(view.updated_at)}
                {who ? ` · ${who}` : ""}
              </p>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col items-start gap-2">
            <Empty>No process written yet.</Empty>
            {view.can_edit ? (
              <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                <PencilIcon aria-hidden="true" />
                Edit
              </Button>
            ) : null}
          </div>
        )}
      </Part>
    </>
  );
}

function HowIWorkDialog({
  api,
  company,
  de,
  view,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  de: DeProfile;
  view: HowIWorkView;
  onClose: () => void;
}) {
  const id = useId();
  const qc = useQueryClient();
  // Codex chart-howiwork r1 #2: the draft's base is what was opened, text and version together;
  // a refetch while editing never moves it, so a save over unseen changes is a 409.
  const [base] = useState(() => ({ text: view.text ?? null, version: view.version }));
  const [text, setText] = useState(base.text ?? "");
  const [error, setError] = useState<string | null>(null);
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: keys.de(company.id, de.membership_id) }),
      qc.invalidateQueries({ queryKey: [...keys.de(company.id, de.membership_id), "how-i-work"] }),
    ]);
  const save = useMutation({
    mutationFn: (next: string | null) =>
      api.client.digitalEmployees.putHowIWork({
        params: { co: company.id, id: de.membership_id },
        body: { text: next, base_version: base.version },
      }),
    onSuccess: async () => {
      await refresh();
      toast.add({ title: "Saved", type: "success" });
      onClose();
    },
    onError: async (e) => {
      // Someone saved in between: keep the draft, reload what is current, say so.
      if (e instanceof ApiError && e.status === 409) {
        setError("Someone else saved a newer version. Copy your text, close and reopen to see it.");
        await refresh();
        return;
      }
      setError(problemText(e, "Not saved."));
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = text.trim() || null;
    if (next === base.text) return onClose();
    save.mutate(next);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>How {workerName(de.display_name)} works</DialogTitle>
            <DialogDescription>Sent to it at its next run. Blank removes it.</DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={`${id}-text`}>Process</FieldLabel>
            <Textarea
              id={`${id}-text`}
              rows={14}
              maxLength={MAX}
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-slot="how-i-work-input"
            />
          </Field>
          {error ? (
            <p role="alert" className="text-sm text-destructive" data-slot="how-i-work-error">
              {error}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function HowIWorkHistory({
  api,
  company,
  deId,
  name,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  deId: string;
  name: string;
  onClose: () => void;
}) {
  // Codex chart-howiwork r1: every version, a page at a time ("Older" reads the next).
  const q = useInfiniteQuery({
    queryKey: [...keys.de(company.id, deId), "how-i-work", "versions"],
    queryFn: ({ pageParam }) =>
      api.client.digitalEmployees.howIWorkVersions({
        params: { co: company.id, id: deId },
        query: { limit: 50, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    initialPageParam: "",
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
  const rows = q.data?.pages.flatMap((p) => p.data) ?? [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>How {name} works · history</DialogTitle>
          <DialogDescription>Every saved version, newest first.</DialogDescription>
        </DialogHeader>
        <div
          className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto"
          data-slot="how-i-work-versions"
        >
          {q.isPending ? (
            <Empty>…</Empty>
          ) : q.isError ? (
            <LoadError what="History" error={q.error} />
          ) : rows.length === 0 ? (
            <Empty>No versions yet.</Empty>
          ) : (
            rows.map((v) => {
              const who = byLine(v.author);
              return (
                <section
                  key={v.version}
                  className="flex min-w-0 flex-col gap-1 border-b border-border pb-3 last:border-b-0"
                  data-version={v.version}
                  aria-label={`Version ${v.version}`}
                >
                  <p className="text-xs text-muted-foreground">
                    Version {v.version} · {formatDateTime(v.created_at)}
                    {who ? ` · ${who}` : ""}
                  </p>
                  {v.text ? (
                    <p className="text-sm whitespace-pre-line text-foreground">{v.text}</p>
                  ) : (
                    <Empty>Cleared.</Empty>
                  )}
                </section>
              );
            })
          )}
          {q.hasNextPage ? (
            <div>
              <Button
                variant="secondary"
                size="sm"
                disabled={q.isFetchingNextPage}
                onClick={() => q.fetchNextPage()}
                data-slot="how-i-work-older"
              >
                {q.isFetchingNextPage ? "Loading…" : "Older versions"}
              </Button>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
